import React from 'react';
import { Sparkles, Loader2, X, BookOpen, Check } from 'lucide-react';

// Popover that hangs above the Inbox composer when the operator hits
// the ✨ "Suggest reply" button. Displays the three drafts the model
// returned (concise / detailed / warm), each with a confidence badge,
// the model's recommendation highlighted, and the KB entries it leaned
// on. Choosing a draft hands it to the composer; the operator can
// always edit before sending.

const TONE_META = {
  concise: { label: 'Concise', tint: 'bg-slate-100 text-slate-700' },
  detailed: { label: 'Detailed', tint: 'bg-indigo-100 text-indigo-700' },
  warm: { label: 'Warm', tint: 'bg-pink-100 text-pink-700' },
};

function confidenceBadge(conf) {
  const v = typeof conf === 'number' ? conf : 0;
  if (v >= 0.85) return { label: `${Math.round(v * 100)}% · grounded`, tint: 'bg-emerald-100 text-emerald-800' };
  if (v >= 0.55) return { label: `${Math.round(v * 100)}% · likely`, tint: 'bg-amber-100 text-amber-800' };
  if (v >= 0.25) return { label: `${Math.round(v * 100)}% · review`, tint: 'bg-orange-100 text-orange-800' };
  return { label: `${Math.round(v * 100)}% · verify`, tint: 'bg-red-100 text-red-800' };
}

const AIReplyPanel = ({
  isOpen,
  isLoading,
  result,         // { drafts, recommendedIndex, reasoning, kbMatchCount, kbMatchedQuestions, model }
  error,
  onPick,         // (text) => void
  onRegenerate,
  onClose,
}) => {
  if (!isOpen) return null;
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 mx-2 z-30">
      <div className="bg-white border border-indigo-200 shadow-xl rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <Sparkles size={14} />
            AI reply suggestions
            {isLoading && <Loader2 size={12} className="animate-spin text-indigo-500 ml-1" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRegenerate}
              disabled={isLoading}
              className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            >
              Regenerate
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-700 rounded"
              aria-label="Close suggestions"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {isLoading && !result && (
          <div className="p-6 text-center text-sm text-gray-500">
            Drafting 3 reply options based on the conversation and your knowledge base…
          </div>
        )}

        {error && (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        )}

        {result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3">
              {(result.drafts || []).map((draft, i) => {
                const meta = TONE_META[draft.tone] || { label: draft.tone, tint: 'bg-gray-100 text-gray-700' };
                const conf = confidenceBadge(draft.confidence);
                const isRecommended = i === (result.recommendedIndex ?? 0);
                return (
                  <div
                    key={i}
                    className={`relative rounded-md border ${isRecommended ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'} bg-white flex flex-col`}
                  >
                    <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.tint}`}>
                        {meta.label}
                      </span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${conf.tint}`}>
                        {conf.label}
                      </span>
                      {isRecommended && (
                        <span className="text-[10px] font-semibold text-indigo-700 inline-flex items-center gap-0.5">
                          <Check size={10} /> pick
                        </span>
                      )}
                    </div>
                    <div className="px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap flex-1 max-h-44 overflow-y-auto">
                      {draft.text}
                    </div>
                    <div className="px-3 py-1.5 border-t border-gray-100 flex justify-end">
                      <button
                        onClick={() => onPick(draft.text)}
                        className="text-xs font-medium px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
                      >
                        Use this
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {(result.reasoning || (result.kbMatchedQuestions && result.kbMatchedQuestions.length > 0)) && (
              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/70 text-[11px] text-gray-600 space-y-1">
                {result.reasoning && (
                  <div><span className="font-medium text-gray-700">Why:</span> {result.reasoning}</div>
                )}
                {result.kbMatchedQuestions?.length > 0 && (
                  <div className="flex items-start gap-1">
                    <BookOpen size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      <span className="font-medium text-gray-700">KB:</span>
                      {result.kbMatchedQuestions.map((m) => (
                        <span key={m.id} className="text-gray-600">· {m.question.slice(0, 80)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {result.model && (
                  <div className="text-gray-400">model: {result.model}</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AIReplyPanel;
